import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../utils/format';
import { useTenant } from '../../context/TenantContext';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import CompensationOverview from './components/CompensationOverview';
import PayBandVisualization from './components/PayBandVisualization';
import PeerComparison from './components/PeerComparison';
import PayHistory from './components/PayHistory';
import InformationRequests from './components/InformationRequests';
import MyProfile from './components/MyProfile';
import BenefitsBreakdown from './components/BenefitsBreakdown';
import { getTotalBenefitsValue } from '../../data/benefitsPackages';
import IdentityVerification from './components/IdentityVerification';

const EmployeePortal = () => {
  const { t, i18n } = useTranslation();
  // Demo dates, computed relative to today so the demo never looks stale.
  // Format follows i18n locale: dd.mm.yyyy for DE, dd/mm/yyyy for EN.
  const fmtDateForDemo = (date) => {
    const d = new Date(date);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return i18n.language === 'de' ? `${dd}.${mm}.${yyyy}` : `${dd}/${mm}/${yyyy}`;
  };
  const today = new Date();
  const monthsAgo = (n) => { const d = new Date(today); d.setMonth(d.getMonth() - n); return d; };
  const monthsAhead = (n) => { const d = new Date(today); d.setMonth(d.getMonth() + n); return d; };
  const daysAhead = (n) => { const d = new Date(today); d.setDate(d.getDate() + n); return d; };

  const { num, eur, date } = useFormatters();
  // Persist identity verification across page refreshes, without this, every
  // navigation back to the employee portal kicks the user back to the SMS gate,
  // which broke the download flow (Download → page reload → re-verify → request gone).
  const VERIFICATION_KEY = 'clearequal.employee.verified.v1';
  const [isVerified, setIsVerified] = useState(() => {
    try {
      return localStorage.getItem(VERIFICATION_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const { tenantId } = useTenant();
  const location = useLocation();

  // Read ?tab=... from URL so deep-links from header dropdown / settings
  // land on the right tab. We support all five tabs.
  const validTabs = ['overview', 'comparison', 'history', 'requests', 'profile'];
  const initialTab = (() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    return validTabs.includes(t) ? t : 'overview';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);

  // Re-sync if the URL changes (e.g. user clicks header dropdown again)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t && validTabs.includes(t)) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Listen for header notification clicks that want to switch tabs.
  // The Header dispatches 'clearequal.switchTab' with detail.tab when the
  // user clicks a notification that lives in a specific tab.
  useEffect(() => {
    const handler = (e) => {
      const tab = e?.detail?.tab;
      if (tab) setActiveTab(tab);
    };
    window.addEventListener('clearequal.switchTab', handler);
    return () => window.removeEventListener('clearequal.switchTab', handler);
  }, []);

  // Compute the benefits package value for the active tenant, this rolls
  // into the Total Compensation figure so workers see the full picture.
  const benefitsTotalEur = getTotalBenefitsValue(tenantId);

  // Mock data for compensation overview
  const compensationData = {
    baseSalary: 75000,
    variablePay: 15000,
    totalCompensation: 75000 + 15000 + benefitsTotalEur,
    effectiveDate: fmtDateForDemo(monthsAgo(3)),
    benefits: [
      'employeePortal.benefits.healthInsurance',
      'employeePortal.benefits.dentalCoverage',
      'employeePortal.benefits.retirementPlan',
      'employeePortal.benefits.lifeInsurance',
      'employeePortal.benefits.flexiblePto',
      'employeePortal.benefits.professionalDevelopment',
      'employeePortal.benefits.remoteWork',
      'employeePortal.benefits.wellnessProgram'
    ]
  };

  // Mock data for pay band visualization
  const payBandData = {
    jobTitle: t('employeePortal.payBandMock.jobTitle'),
    department: t('employeePortal.payBandMock.department'),
    location: t('employeePortal.payBandMock.location'),
    bandLevel: "L5 - Senior",
    minSalary: 65000,
    maxSalary: 95000,
    currentSalary: 75000,
    nextReview: fmtDateForDemo(monthsAhead(2))
  };

  // Mock data for peer comparison, keyed by metric so the card actually
  // shows different data when the user switches Total / Base / Variable.
  const comparisonData = {
    totalComp: {
      sampleSize: 47,
      marketMedian: 78000,
      yourValue: 90000,
      yourPercentile: 65,
      distributions: [
        { range: "€60–65k", count: 8,  includesYou: false },
        { range: "€65–70k", count: 12, includesYou: false },
        { range: "€70–75k", count: 15, includesYou: false },
        { range: "€75–80k", count: 8,  includesYou: false },
        { range: "€80–85k", count: 3,  includesYou: false },
        { range: "€85–90k", count: 1,  includesYou: true  }
      ]
    },
    baseSalary: {
      sampleSize: 47,
      marketMedian: 68000,
      yourValue: 75000,
      yourPercentile: 72,
      distributions: [
        { range: "€55–60k", count: 5,  includesYou: false },
        { range: "€60–65k", count: 11, includesYou: false },
        { range: "€65–70k", count: 14, includesYou: false },
        { range: "€70–75k", count: 10, includesYou: true  },
        { range: "€75–80k", count: 5,  includesYou: false },
        { range: "€80–85k", count: 2,  includesYou: false }
      ]
    },
    variablePay: {
      sampleSize: 47,
      marketMedian: 12000,
      yourValue: 15000,
      yourPercentile: 68,
      distributions: [
        { range: "€5–8k",   count: 6,  includesYou: false },
        { range: "€8–11k",  count: 10, includesYou: false },
        { range: "€11–14k", count: 13, includesYou: false },
        { range: "€14–17k", count: 9,  includesYou: true  },
        { range: "€17–20k", count: 6,  includesYou: false },
        { range: "€20–25k", count: 3,  includesYou: false }
      ]
    }
  };

  // Mock data for pay history
  const historyData = {
    upcomingReview: {
      date: fmtDateForDemo(monthsAhead(2)),
      type: t('employeePortal.history.reviewType'),
      daysUntil: 60
    },
    timeline: [
      {
        id: 1,
        type: "review",
        date: fmtDateForDemo(monthsAgo(4)),
        description: t('employeePortal.historyMock.reviewQ4'),
        additionalInfo: ""
      },
      {
        id: 2,
        type: "adjustment",
        date: fmtDateForDemo(monthsAgo(7)),
        description: t('employeePortal.historyMock.adjustmentTitle'),
        financialDetails: {
          previousAmount: 72000,
          newAmount: 75000,
          increase: 3000,
          percentage: "4.2"
        },
        additionalInfo: t('employeePortal.historyMock.adjustmentInfo')
      },
      {
        id: 3,
        type: "bonus",
        date: fmtDateForDemo(monthsAgo(10)),
        description: t('employeePortal.historyMock.bonusTitle'),
        financialDetails: {
          newAmount: 5000
        },
        additionalInfo: t('employeePortal.historyMock.bonusInfo')
      },
      {
        id: 4,
        type: "promotion",
        date: fmtDateForDemo(monthsAgo(14)),
        description: t('employeePortal.historyMock.promotionTitle'),
        financialDetails: {
          previousAmount: 65000,
          newAmount: 72000,
          increase: 7000,
          percentage: "10.8"
        },
        additionalInfo: t('employeePortal.historyMock.promotionInfo')
      }
    ],
    summary: {
      totalIncreases: 3,
      averageIncrease: 7.5,
      yearsWithCompany: 2.5
    }
  };

  // Mock data for information requests
  const requestsData = [
    {
      id: 1,
      title: t('employeePortal.requestsMock.completedTitle'),
      description: t('employeePortal.requestsMock.completedDescription'),
      status: "completed",
      requestDate: fmtDateForDemo(monthsAgo(2)),
      completedDate: fmtDateForDemo(monthsAgo(1)),
      downloadUrl: "#"
    },
    {
      id: 2,
      title: t('employeePortal.requestsMock.processingTitle'),
      description: t('employeePortal.requestsMock.processingDescription'),
      status: "processing",
      requestDate: fmtDateForDemo(monthsAgo(0)),
      estimatedCompletion: fmtDateForDemo(daysAhead(7))
    }
  ];

  const tabs = [
    { id: 'overview', labelKey: 'employeePortal.tabs.overview', icon: 'Home' },
    { id: 'comparison', labelKey: 'employeePortal.tabs.peerComparison', icon: 'Users' },
    { id: 'history', labelKey: 'employeePortal.tabs.payHistory', icon: 'Clock' },
    { id: 'requests', labelKey: 'employeePortal.tabs.informationRequests', icon: 'FileText' },
    { id: 'profile', labelKey: 'employeePortal.tabs.profile', icon: 'UserCircle' }
  ];

  const handleVerificationComplete = (verified) => {
    setIsVerified(verified);
    try {
      localStorage.setItem(VERIFICATION_KEY, verified ? 'true' : 'false');
    } catch {}
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground break-words">{t('employeePortal.pageTitle')}</h1>
              <p className="text-muted-foreground mt-2 break-words">
                {t('employeePortal.subtitle')}
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <div className="flex items-center space-x-2 px-4 py-2 bg-card border border-border rounded-lg">
                <Icon name="Shield" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">{t('employeePortal.badges.euCompliant')}</span>
              </div>
              
              {isVerified && (
                <div className="flex items-center space-x-2 px-4 py-2 bg-success/10 border border-success/20 rounded-lg">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm font-medium text-success">{t('employeePortal.badges.identityVerified')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Identity Verification */}
          {!isVerified && (
            <div className="mb-8">
              <IdentityVerification 
                onVerificationComplete={handleVerificationComplete}
                isRequired={false}
              />
            </div>
          )}

          {/* Content Area */}
          {isVerified ? (
            <>
              {/* Navigation Tabs */}
              <div className="flex flex-wrap gap-2 mb-8 border-b border-border">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-smooth ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{t(tab.labelKey)}</span>
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="space-y-8">
                {activeTab === 'overview' && (
                  <>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                      <div className="space-y-8">
                        <CompensationOverview compensationData={compensationData} />
                        <PayBandVisualization payBandData={payBandData} />
                      </div>
                      <div>
                        <PeerComparison comparisonData={comparisonData} />
                      </div>
                    </div>
                    <BenefitsBreakdown tenantId={tenantId} />
                  </>
                )}

                {activeTab === 'comparison' && (
                  <PeerComparison comparisonData={comparisonData} />
                )}

                {activeTab === 'history' && (
                  <PayHistory historyData={historyData} />
                )}

                {activeTab === 'requests' && (
                  <InformationRequests />
                )}

                {activeTab === 'profile' && (
                  <MyProfile />
                )}
              </div>
            </>
          ) : (
            /* Limited Access View */
            (<div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              <div className="space-y-8">
                <CompensationOverview compensationData={compensationData} />
                
                {/* Blurred Pay Band */}
                <div className="relative">
                  <div className="filter blur-sm pointer-events-none">
                    <PayBandVisualization payBandData={payBandData} />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                    <div className="text-center">
                      <Icon name="Lock" size={48} className="text-muted-foreground mx-auto mb-3" />
                      <p className="font-medium text-foreground">{t('employeePortal.gate.identityRequired')}</p>
                      <p className="text-sm text-muted-foreground">{t('employeePortal.gate.identityRequiredBody')}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="filter blur-sm pointer-events-none">
                  <PeerComparison comparisonData={comparisonData} />
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                  <div className="text-center">
                    <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium text-foreground">{t('employeePortal.gate.peerLocked')}</p>
                    <p className="text-sm text-muted-foreground">{t('employeePortal.gate.peerLockedBody')}</p>
                  </div>
                </div>
              </div>
            </div>)
          )}

          {/* Footer Information */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="bg-muted/20 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Icon name="Info" size={20} className="text-primary mt-0.5" />
                <div>
                  <h3 className="font-medium text-foreground mb-2">{t('employeePortal.rights.title')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                    <div>
                      <h4 className="font-medium text-foreground mb-1">{t('employeePortal.rights.infoTitle')}</h4>
                      <ul className="space-y-1">
                        <li>• {t('employeePortal.rights.info1')}</li>
                        <li>• {t('employeePortal.rights.info2')}</li>
                        <li>• {t('employeePortal.rights.info3')}</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-1">{t('employeePortal.rights.protectionTitle')}</h4>
                      <ul className="space-y-1">
                        <li>• {t('employeePortal.rights.protection1')}</li>
                        <li>• {t('employeePortal.rights.protection2')}</li>
                        <li>• {t('employeePortal.rights.protection3')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployeePortal;